/**
 * Pure derived-data functions extracted from projectStore.
 * No store imports — these are independently testable.
 */
import type { TimelineProject, TimelineScene, TimelineShot } from '@/types/timeline.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';

export function allShots(project: TimelineProject): TimelineShot[] {
  return project.scenes.filter(s => !s.isOmitted).flatMap(s => s.shots);
}

export function getShotAtTime(project: TimelineProject, t: number): TimelineShot | null {
  const shots = allShots(project);
  let cumulative = 0;
  for (const shot of shots) {
    if (t >= cumulative && t < cumulative + shot.duration) return shot;
    cumulative += shot.duration;
  }
  return shots.length > 0 ? shots[shots.length - 1] : null;
}

export function getShotStartTime(project: TimelineProject, id: number): number {
  const shots = allShots(project);
  let cumulative = 0;
  for (const shot of shots) {
    if (shot.id === id) return cumulative;
    cumulative += shot.duration;
  }
  return 0;
}

export function getSceneForShot(
  project: TimelineProject,
  shotId: number,
): { color: string; name: string; locationType: string; timeOfDay: string } | null {
  for (const scene of project.scenes) {
    if (scene.shots.some(s => s.id === shotId)) {
      return {
        color: scene.color,
        name: scene.name,
        locationType: scene.locationType,
        timeOfDay: scene.timeOfDay,
      };
    }
  }
  return null;
}

export function getActiveScenes(
  project: TimelineProject,
  activeSceneId: number | null,
): TimelineScene[] {
  if (activeSceneId === null) return project.scenes.filter(s => !s.isOmitted);
  return project.scenes.filter(s => s.id === activeSceneId && !s.isOmitted);
}

export function getViewOffset(
  project: TimelineProject,
  activeSceneId: number | null,
): number {
  if (activeSceneId === null) return 0;
  let offset = 0;
  for (const scene of project.scenes) {
    if (scene.id === activeSceneId) break;
    if (scene.isOmitted) continue;
    offset += scene.shots.reduce((sum, s) => sum + s.duration, 0);
  }
  return offset;
}

export function getViewDuration(
  project: TimelineProject,
  activeSceneId: number | null,
): number {
  if (activeSceneId === null) return project.totalDuration;
  const scene = project.scenes.find(s => s.id === activeSceneId);
  if (!scene) return project.totalDuration;
  return scene.shots.reduce((sum, s) => sum + s.duration, 0);
}

export function getScriptProgress(
  project: TimelineProject,
  activeSceneId: number | null,
  currentTime: number,
  viewOffsetVal: number,
): { scene: TimelineScene | null; progress: number } {
  const scenes = getActiveScenes(project, activeSceneId);
  if (scenes.length === 0) return { scene: null, progress: 0 };

  let offset = viewOffsetVal;
  for (const scene of scenes) {
    const sceneDuration = scene.shots.reduce((s, shot) => s + shot.duration, 0);
    if (currentTime < offset + sceneDuration) {
      const elapsed = currentTime - offset;
      return { scene, progress: sceneDuration > 0 ? elapsed / sceneDuration : 0 };
    }
    offset += sceneDuration;
  }

  return { scene: scenes[scenes.length - 1], progress: 1 };
}

/** Compute absolute timeline position for an audio clip. */
export function getClipAbsoluteStart(
  clip: { shotId: number; shotOffset: number },
  project: TimelineProject,
): number {
  return getShotStartTime(project, clip.shotId) + clip.shotOffset;
}

/** Given an absolute timeline position, resolve to shotId + shotOffset. */
export function resolveClipPlacement(
  absoluteTime: number,
  project: TimelineProject,
): { shotId: number; shotOffset: number } {
  let cumulative = 0;
  for (const scene of project.scenes) {
    if (scene.isOmitted) continue;
    for (const shot of scene.shots) {
      const shotEnd = cumulative + shot.duration;
      if (absoluteTime >= cumulative && absoluteTime < shotEnd) {
        return { shotId: shot.id, shotOffset: absoluteTime - cumulative };
      }
      cumulative = shotEnd;
    }
  }
  // Past all shots — anchor to last shot
  const shots = allShots(project);
  if (shots.length > 0) {
    const last = shots[shots.length - 1];
    const lastStart = cumulative - last.duration;
    return { shotId: last.id, shotOffset: absoluteTime - lastStart };
  }
  return { shotId: 0, shotOffset: absoluteTime };
}

export function getAssetsForScene(
  project: TimelineProject,
  sceneId: number,
  generationHistory: GenerationHistoryEntry[],
): { videos: number; images: number; totalCredits: number } {
  const scene = project.scenes.find(s => s.id === sceneId);
  if (!scene) return { videos: 0, images: 0, totalCredits: 0 };
  const shotIds = new Set(scene.shots.map(s => s.id));
  const entries = generationHistory.filter(
    h => h.shotId !== null && shotIds.has(h.shotId)
  );
  return {
    videos: entries.filter(h => h.assetType === 'video' || h.assetType === 'transition_video').length,
    images: entries.filter(h => h.assetType === 'image').length,
    totalCredits: entries.reduce((sum, h) => sum + h.costCredits, 0),
  };
}

/**
 * Whether a shot has a renderable video associated. In standalone mode the
 * indicator is `videoObjectUrl` (a Blob URL set when video bytes load).
 * In embedded mode where the web does not load video bytes, an extra flag
 * `hasEmbeddedVideo` is surfaced on the in-memory model when the underlying
 * shot row has video data on disk.
 */
export function shotHasVideo(shot: TimelineShot): boolean {
  if (shot.videoObjectUrl) return true;
  // hasEmbeddedVideo is optional on the type; cast guards against absence.
  return Boolean((shot as { hasEmbeddedVideo?: boolean }).hasEmbeddedVideo);
}

/**
 * Whether a shot has a thumbnail / static image to export. In standalone mode
 * the indicator is `thumbnailDataUrl` (a data: or blob: URL backing the in-memory
 * thumbnail). In embedded mode the web does not load thumbnail bytes — an extra
 * flag `hasEmbeddedImage` is surfaced on the in-memory model when the
 * underlying shot has a thumbnail in the disk cache or shots.thumbnail BLOB.
 */
export function shotHasImage(shot: TimelineShot): boolean {
  if (shot.thumbnailDataUrl) return true;
  // hasEmbeddedImage is optional on the type; cast guards against absence.
  return Boolean((shot as { hasEmbeddedImage?: boolean }).hasEmbeddedImage);
}
